<template>
  <div class="ai-chat-fab" :class="{ 'with-offset': withOffset }">
    <q-avatar
      size="48px"
      class="ai-chat-avatar-btn"
      :aria-label="isOpen ? 'Close AI Chat' : 'Open AI Chat'"
      @click="toggle"
    >
      <img :src="aiAvatarUrl" :alt="currentPersona.displayName" />
      <q-tooltip
        anchor="top middle"
        self="bottom middle"
        :offset="[0, 10]"
        class="ai-chat-fab-tooltip"
        :style="tooltipStyle"
        @before-show="pickTooltipPhrase"
      >
        {{ tooltipPhrase }}
      </q-tooltip>
    </q-avatar>
  </div>
  <AiChatPanel />
</template>

<script setup>
  import { computed, ref, watch } from 'vue'
  import { useAiChatStore } from '@/stores/aiChatStore'
  import AiChatPanel from './AiChatPanel.vue'
  import { registerDefaultAiTools } from '@/services/ai/tools/registerDefaultTools'

  const TOOLTIP_PHRASES = Object.freeze({
    dobby: [
      'Ohh that tickles!',
      'Psst... need a quest idea?',
      'Ask me about monsters, notes, or chaos.',
      'Dobby is listening... always.',
      'Need lore? I have lore.',
      'Hover detected. Mischief enabled.',
      'Click me. I promise only mild mayhem.'
    ],
    marvin: [
      'Here I am, brain the size of a planet…',
      'I suppose you need something.',
      'Monsters. Spells. Existential dread. Take your pick.',
      'Go on then. I\'ll help. I always help.',
      'Forty-two. There, that\'s the answer. Now what was the question?',
      'I could calculate your odds of survival, but why bother.',
      'Click me. It won\'t make either of us happy.'
    ]
  })

  const store = useAiChatStore()
  const isOpen = computed(() => store.isOpen)
  const aiAvatarUrl = computed(() => store.aiAvatarUrl)
  const currentPersona = computed(() => store.currentPersona)
  const toggle = () => store.toggleOpen()

  const tooltipStyle = Object.freeze({
    fontSize: '15px',
    lineHeight: '1.5',
    whiteSpace: 'nowrap',
    letterSpacing: '0'
  })

  const lastTooltipIndex = ref(0)
  const tooltipPhrase = ref('')

  const getPhrasesForPersona = () =>
    TOOLTIP_PHRASES[currentPersona.value?.id] || TOOLTIP_PHRASES.dobby

  // Reset to first phrase when persona changes
  watch(currentPersona, () => {
    tooltipPhrase.value = getPhrasesForPersona()[0]
    lastTooltipIndex.value = 0
  }, { immediate: true })

  const pickTooltipPhrase = () => {
    const phrases = getPhrasesForPersona()
    if (phrases.length < 2) return
    let next = lastTooltipIndex.value
    while (next === lastTooltipIndex.value) {
      next = Math.floor(Math.random() * phrases.length)
    }
    lastTooltipIndex.value = next
    tooltipPhrase.value = phrases[next]
  }

  // Ensure default tools are registered once when button mounts
  try {
    registerDefaultAiTools()
  } catch {
    /* ignore duplicate registration */
  }

  const withOffset = false
</script>

<style scoped>
  .ai-chat-fab {
    position: fixed;
    right: 16px;
    bottom: 16px;
    z-index: 3000; /* align with/above dice roller overlay */
  }

  .ai-chat-avatar-btn {
    cursor: pointer;
    box-shadow: 0 3px 5px rgba(0, 0, 0, 0.3);
    transition: transform 0.2s ease;
  }
  .ai-chat-avatar-btn:hover {
    transform: scale(1.1);
  }
</style>
